import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, Tag, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Blog List Page
export const BlogPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await axios.get(`${API}/blogs`);
        setBlogs(response.data);
      } catch (error) {
        console.error('Error fetching blogs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  const filteredBlogs = filter === 'all' 
    ? blogs 
    : blogs.filter(b => b.category === filter);

  const formatCategory = (category) => {
    return category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div data-testid="blog-page" className="min-h-screen pt-28 pb-16 bg-cream">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-terracotta font-mono text-sm uppercase tracking-wider">
            Stories & Inspiration
          </span>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-forest mt-2 mb-4">
            The Reggae Essences Journal
          </h1>
          <p className="text-warm-brown text-lg max-w-2xl mx-auto">
            Dive into the world of natural beauty, Caribbean traditions, and the stories behind our handcrafted products.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {['all', 'soaps', 'body_butters', 'candles'].map((category) => (
            <button
              key={category}
              onClick={() => setFilter(category)}
              className={`px-5 py-2 rounded-full font-medium transition-all ${
                filter === category
                  ? 'bg-forest text-white'
                  : 'bg-white text-forest hover:bg-sand'
              }`}
            >
              {category === 'all' ? 'All Posts' : formatCategory(category)}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                <div className="h-48 bg-gray-200" />
                <div className="p-6 space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-1/4" />
                  <div className="h-6 bg-gray-200 rounded" />
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredBlogs.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-500 text-lg">No blog posts found in this category.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredBlogs.map((blog) => (
              <Link
                key={blog.id}
                to={`/blog/${blog.slug}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={blog.image || 'https://images.unsplash.com/photo-1556228720-195a672e8a03?w=800'}
                    alt={blog.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-3 py-1 bg-sand text-warm-brown text-xs font-medium rounded-full">
                      {formatCategory(blog.category)}
                    </span>
                    <span className="text-gray-400 text-sm flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {formatDate(blog.created_at)}
                    </span>
                  </div>
                  <h2 className="font-heading text-xl font-semibold text-forest group-hover:text-gold transition-colors line-clamp-2 mb-3">
                    {blog.title}
                  </h2>
                  <p className="text-gray-600 text-sm line-clamp-3 mb-4">
                    {blog.excerpt}
                  </p>
                  <span className="text-terracotta font-medium text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                    Read More <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Single Blog Post Page
export const BlogPostPage = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedBlogs, setRelatedBlogs] = useState([]);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const response = await axios.get(`${API}/blogs/${slug}`);
        setBlog(response.data);
        
        // Fetch related blogs
        const allBlogs = await axios.get(`${API}/blogs`);
        const related = allBlogs.data
          .filter(b => b.slug !== slug && b.category === response.data.category)
          .slice(0, 2);
        setRelatedBlogs(related);
      } catch (error) {
        console.error('Error fetching blog:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [slug]);

  const formatCategory = (category) => {
    return category?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-28 pb-16 bg-cream">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 rounded w-3/4" />
            <div className="h-64 bg-gray-200 rounded-2xl" />
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded" />
              <div className="h-4 bg-gray-200 rounded w-5/6" />
              <div className="h-4 bg-gray-200 rounded w-4/6" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen pt-28 pb-16 bg-cream">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h1 className="font-heading text-3xl font-bold text-forest mb-4">
            Blog Post Not Found
          </h1>
          <p className="text-gray-600 mb-8">
            The post you're looking for doesn't exist or has been removed.
          </p>
          <Link to="/blog">
            <Button className="bg-forest text-white rounded-full">
              Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="blog-post-page" className="min-h-screen pt-28 pb-16 bg-cream">
      <article className="max-w-4xl mx-auto px-6 md:px-12">
        {/* Back Link */}
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-forest mb-8 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
          Back to Journal
        </Link>

        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="px-4 py-1.5 bg-forest text-white text-sm font-medium rounded-full">
              {formatCategory(blog.category)}
            </span>
            <span className="text-gray-500 flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {formatDate(blog.created_at)}
            </span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-forest leading-tight">
            {blog.title}
          </h1>
        </header>

        {/* Featured Image */}
        {blog.image && (
          <div className="mb-10 rounded-2xl overflow-hidden shadow-lg">
            <img
              src={blog.image}
              alt={blog.title}
              className="w-full h-auto"
            />
          </div>
        )}

        {/* Content */}
        <div 
          className="prose prose-lg max-w-none
            prose-headings:font-heading prose-headings:text-forest
            prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
            prose-p:text-gray-700 prose-p:leading-relaxed
            prose-a:text-terracotta prose-a:no-underline hover:prose-a:underline
            prose-strong:text-forest
            prose-ul:list-disc prose-ul:pl-6
            prose-li:text-gray-700
            prose-blockquote:border-l-4 prose-blockquote:border-gold prose-blockquote:bg-sand prose-blockquote:py-2 prose-blockquote:px-4 prose-blockquote:italic
          "
          dangerouslySetInnerHTML={{ __html: blog.content }}
        />

        {/* Share & CTA */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="bg-forest text-white rounded-2xl p-8 text-center">
            <h3 className="font-heading text-2xl font-bold mb-3">
              Ready to Experience the Magic?
            </h3>
            <p className="text-cream/80 mb-6">
              Explore our collection of handcrafted products, made with love and the finest natural ingredients.
            </p>
            <Link to="/shop">
              <Button className="bg-gold hover:bg-gold/90 text-forest rounded-full px-8 py-6 font-semibold">
                Shop Now
              </Button>
            </Link>
          </div>
        </div>

        {/* Related Posts */}
        {relatedBlogs.length > 0 && (
          <div className="mt-16">
            <h3 className="font-heading text-2xl font-bold text-forest mb-6">
              More Stories You'll Love
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {relatedBlogs.map((related) => (
                <Link
                  key={related.id}
                  to={`/blog/${related.slug}`}
                  className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all"
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={related.image || 'https://images.unsplash.com/photo-1556228720-195a672e8a03?w=800'}
                      alt={related.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-4">
                    <h4 className="font-heading font-semibold text-forest group-hover:text-gold transition-colors line-clamp-2">
                      {related.title}
                    </h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
};
